import { z } from 'zod';
import type { CancellationFieldMeta } from '../services/cancellationRules';

// Fallback ONLY, for the pre-load / missing-option case. The live enum options are sourced
// from each string field's validation_rules.in (Decision D3); a future seeder widening the
// enum needs no code change.
export const BALANCE_ADJUSTMENT_FALLBACK = ['pro_rata', 'full_refund', 'no_adjustment', 'write_off'] as const;

export interface CancellationRulesFormValues {
    cancellations_allowed: boolean;
    // integers held as raw input strings, enums as value strings, the one boolean as boolean
    fields: Record<string, string | boolean>;
}

/**
 * Build the schema from the LIVE field metadata. Unlike CTHF (which uses a plain z.object —
 * see its D11), a factory is justified HERE because the per-field rules genuinely fire on
 * user input and their errors ARE rendered on the controls:
 *   - integer fields: coerce string -> number, enforce integer + the catalogue min/max,
 *     but allow empty (is_required false) so a blank field is valid and simply not written;
 *   - string enum fields: membership against the LIVE allowed set (banner pattern), allow
 *     empty (optional);
 *   - the boolean field: z.boolean().
 * All field rules are gated on cancellations_allowed — disabling relaxes everything (the
 * fields are hidden). The backend ConfigSettingValidator re-enforces the same rules per key,
 * so this client schema is convenience over an owned boundary — but real convenience.
 */
export const makeCancellationRulesSchema = (fieldMetas: CancellationFieldMeta[]) =>
    z
        .object({
            cancellations_allowed: z.boolean(),
            fields: z.record(z.string(), z.union([z.string(), z.boolean()])),
        })
        .superRefine((values, ctx) => {
            if (!values.cancellations_allowed) return; // relaxed when disabled

            for (const meta of fieldMetas) {
                const raw = values.fields[meta.key];

                if (meta.control === 'number') {
                    if (raw === '' || raw === undefined) continue; // optional / not written
                    const n = Number(raw);
                    const bad =
                        !Number.isInteger(n) ||
                        (meta.min !== undefined && n < meta.min) ||
                        (meta.max !== undefined && n > meta.max);
                    if (bad) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['fields', meta.key],
                            message:
                                meta.min !== undefined && meta.max !== undefined
                                    ? `Enter a whole number between ${meta.min} and ${meta.max}`
                                    : 'Enter a whole number',
                        });
                    }
                } else if (meta.control === 'select') {
                    if (raw === '' || raw === undefined) continue; // optional / not written
                    if (!meta.options?.includes(String(raw))) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['fields', meta.key],
                            message: 'Select a valid option',
                        });
                    }
                }
                // control === 'checkbox' -> boolean, nothing to assert
            }
        });

export type CancellationRulesSchema = ReturnType<typeof makeCancellationRulesSchema>;
